import { z } from "zod";

export enum DiscountReductionType {
  PERCENTAGE = "PERCENTAGE",
  MONETARY_AMOUNT = "MONETARY_AMOUNT",
}

export enum PenaltyAppliedEnum {
  IGNORE = "IGNORE",
  HAS_PENALTY_APPLIED = "HAS_PENALTY_APPLIED",
  NO_PENALTY_APPLIED = "NO_PENALTY_APPLIED",
}
export enum GuaranteeAppliedEnum {
  IGNORE = "IGNORE",
  HAS_GUARANTEE_APPLIED = "HAS_GUARANTEE_APPLIED",
  NO_GUARANTEE_APPLIED = "NO_GUARANTEE_APPLIED",
}
export enum DeltaTypeEnum {
  PERCENTAGE = "PERCENTAGE",
  MONETARY_AMOUNT = "MONETARY_AMOUNT",
}
export enum SellingTargetTypeEnum {
  ACCOMMODATION = "ACCOMMODATION",
  MEAL_PLAN = "MEAL_PLAN",
  EXTRA = "EXTRA",
}

export enum MealPlanUpgradeModeEnum {
  EXTRA = "EXTRA",
  INVENTORY = "INVENTORY",
}
export enum SellingLocationEnum {
  CHECKOUT = "CHECKOUT",
  NOTIFICATION = "NOTIFICATION",
  BOOKING_CONFIRMATION = "BOOKING_CONFIRMATION",
}
export enum SellingConfigurationTypeEnum {
  UPSELLING = "UPSELLING",
  CROSS_SELLING = "CROSS_SELLING",
}
export enum NotificationChannelEnum {
  EMAIL = "EMAIL",
  SMS = "SMS",
  WHATSAPP = "WHATSAPP",
}

export const dateSchema = z.preprocess(arg => {
  if (typeof arg == "string") {
    return new Date(arg);
  }
  if (arg instanceof Date) {
    return arg;
  }
}, z.date());

// 1. Enums as Zod schemas
export const DiscountReductionTypeSchema = z.nativeEnum(DiscountReductionType);
export const PenaltyAppliedEnumSchema = z.nativeEnum(PenaltyAppliedEnum);
export const GuaranteeAppliedEnumSchema = z.nativeEnum(GuaranteeAppliedEnum);
export const DeltaTypeEnumSchema = z.nativeEnum(DeltaTypeEnum);
export const SellingTargetTypeEnumSchema = z.nativeEnum(SellingTargetTypeEnum);
export const MealPlanUpgradeModeEnumSchema = z.nativeEnum(MealPlanUpgradeModeEnum);
export const SellingLocationEnumSchema = z.nativeEnum(SellingLocationEnum);
export const SellingConfigurationTypeEnumSchema = z.nativeEnum(SellingConfigurationTypeEnum);
export const NotificationChannelEnumSchema = z.nativeEnum(NotificationChannelEnum);

// 2. Reusable schemas
// MonetaryAmount can be reused in different places where { amount: number; currency: string } is allowed
export const MonetaryAmountSchema = z.object({
  amount: z.number(),
  currency: z.string(),
});

// 3. AmountDelta
// value can be either a number or a MonetaryAmount object
export const AmountDeltaSchema = z.object({
  deltaType: DeltaTypeEnumSchema,
  value: z.union([z.number(), MonetaryAmountSchema]),
});

// 4. DiscountApplied
export const DiscountAppliedSchema = z.object({
  rackRateDiscountReductionType: DiscountReductionTypeSchema,
  reduction: z.union([z.number(), MonetaryAmountSchema]),
  promoCodes: z.array(z.string()),
});

// 5. SellingActivation
// We'll define a reusable date range schema for excluded/included dates
const DateRangeSchema = z.object({
  start: dateSchema,
  end: dateSchema,
});

const baseActivationRuleSchema = {
  minStayInDays: z.number().optional(),
  maxStayInDays: z.number().optional(),
  minSourceAvailability: z.number().optional(),
  maxSourceAvailability: z.number().optional(),
  minUpgradeToAvailability: z.number().optional(),
  maxUpgradeToAvailability: z.number().optional(),
  minAmountDelta: AmountDeltaSchema.optional(),
  maxAmountDelta: AmountDeltaSchema.optional(),
  excludedDates: z.array(DateRangeSchema),
  includedDates: z.array(DateRangeSchema),
  penaltyApplied: PenaltyAppliedEnumSchema,
  guaranteeApplied: GuaranteeAppliedEnumSchema,
  includedTags: z.array(z.string()),
  promoCodes: z.array(z.string()),
};

export const SellingActivationSchema = z.object({
  ...baseActivationRuleSchema,
  maxReleaseInDays: z.number(),
  minReleaseInDays: z.number().optional(),
});

export const SellingTargetActivationSchema = z.object({
  ...baseActivationRuleSchema,
  maxReleaseInDays: z.number().optional(),
  minReleaseInDays: z.number().optional(),
});

// 6. SellingTarget
export const SellingTargetSchema = z.object({
  targetType: SellingTargetTypeEnumSchema,
  baseAccommodationTicker: z.string().optional(),
  upgradeableToTickers: z.array(z.string()).optional(),
  baseMealPlanTicker: z.string().optional(),
  mealPlanUpgradeMode: MealPlanUpgradeModeEnumSchema.optional(),
  inventories: z.array(z.string()).optional(),
  includedExtraTickers: z.array(z.string()).optional(),
  excludedExtraTickers: z.array(z.string()).optional(),
  sellingActivationRule: SellingTargetActivationSchema.optional(),
  discountApplied: DiscountAppliedSchema.optional(),
});

// 7. SellingConfigurationEntity
export const SellingConfigurationSchema = z.object({
  id: z.string(),
  active: z.boolean(),
  propertyTicker: z.string(),
  propertyRecipients: z.array(z.string()),
  sellingLocations: z.array(SellingLocationEnumSchema),
  notificationChannels: z.array(NotificationChannelEnumSchema),
  sellingConfigurationType: SellingConfigurationTypeEnumSchema,
  sellingTargets: z.array(SellingTargetSchema),
  generalSellingActivationRule: SellingActivationSchema,
  discountApplied: DiscountAppliedSchema.optional(),
  createdAt: dateSchema,
  updatedAt: dateSchema,
});

export type SellingConfigurationDto = z.infer<typeof SellingConfigurationSchema>;
// 8. Infer the TypeScript interface from the Zod schema
export type SellingConfigurationEntity = z.infer<typeof SellingConfigurationSchema> & {
  serialized: string;
};
